import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateRussiaCarrierBody,
  RussiaCarrier,
  UpdateRussiaCarrierBody,
} from '@magimp/shared';
import {
  Button,
  DataTable,
  EditorModal,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Select,
  ToolbarActions,
} from '../../../ui/index.js';
import { CancelButton, EditorFooter, RussiaCarrierForm } from './RussiaCarrierForm.js';
import {
  archiveRussiaCarrier,
  createRussiaCarrier,
  listRussiaCarriers,
  patchRussiaCarrier,
} from '../../../api/russia-carriers.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { RUSSIA_CARRIERS_CATALOG_QK } from '../queries.js';

type StatusFilter = 'active' | 'inactive' | 'all';

export function RussiaCarriersPage() {
  const { t } = useTranslation(['russiaCarriers', 'common']);
  // WHY: route is gated on `russiaCarriers.manage`, but the defensive
  //   check matches the dictionary reference (RoadOptionsPage) so any
  //   future relaxation of the route gate cannot silently leak
  //   create/edit/archive affordances to read-only roles.
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = hasPermission(permissions, 'russiaCarriers.manage');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<RussiaCarrier | null>(null);

  const query = useQuery({
    queryKey: [...RUSSIA_CARRIERS_CATALOG_QK, status, q],
    queryFn: () => listForFilter(status, q),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>
          {t('russiaCarriers:title')}
        </h1>
        <ToolbarActions>
          <Input
            value={q}
            onChange={setQ}
            icon="search"
            placeholder={t('russiaCarriers:search')}
            maxLength={200}
          />
          <Select
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            options={[
              { value: 'active', label: t('russiaCarriers:filters.active') },
              { value: 'inactive', label: t('russiaCarriers:filters.inactive') },
              { value: 'all', label: t('russiaCarriers:filters.all') },
            ]}
            style={{ width: 150 }}
          />
          {canManage && (
            <Button kind="primary" icon="plus" onClick={() => setCreateOpen(true)}>
              {t('russiaCarriers:actions.create')}
            </Button>
          )}
        </ToolbarActions>
      </header>
      {query.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      )}
      {query.isPending && <LoadingState />}
      {query.isSuccess && (
        <DataTable
          rows={query.data}
          onSelect={setSelected}
          emptyState={
            <EmptyState title={t('common:states.emptyTitle')} text={t('russiaCarriers:empty')} />
          }
          columns={[
            { key: 'name', label: t('russiaCarriers:columns.name'), sortable: true },
            {
              key: 'contacts',
              label: t('russiaCarriers:columns.contacts'),
              // WHY: contacts is free-form multi-line PII; the table cell
              //   renders the first line only with overflow ellipsis so a
              //   long list does not blow out row height. The full value
              //   is visible inside the editor modal.
              render: (row) => (
                <span
                  style={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 360,
                  }}
                  title={row.contacts}
                >
                  {row.contacts.split('\n')[0]}
                </span>
              ),
            },
            {
              key: 'status',
              label: t('russiaCarriers:columns.status'),
              width: 130,
              sortable: true,
              sortValue: (row) => (row.active ? 1 : 0),
              render: (row) =>
                row.active
                  ? t('russiaCarriers:status.active')
                  : t('russiaCarriers:status.inactive'),
            },
          ]}
        />
      )}
      {createOpen && canManage && <RussiaCarrierCreateModal onClose={() => setCreateOpen(false)} />}
      {selected && (
        <RussiaCarrierEditorModal
          row={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
          onUpdated={setSelected}
        />
      )}
    </div>
  );
}

async function listForFilter(status: StatusFilter, q: string): Promise<RussiaCarrier[]> {
  const base: { search?: string } = {};
  if (q.trim()) base.search = q.trim();
  if (status === 'active') return (await listRussiaCarriers({ ...base, active: true })).items;
  if (status === 'inactive') return (await listRussiaCarriers({ ...base, active: false })).items;
  return (await listRussiaCarriers({ ...base })).items;
}

function invalidateRussiaCarrierCaches(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: RUSSIA_CARRIERS_CATALOG_QK });
}

function RussiaCarrierCreateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['russiaCarriers', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [name, setName] = useState('');
  const [contacts, setContacts] = useState('');

  const payload: CreateRussiaCarrierBody | null = useMemo(() => {
    const n = name.trim();
    const c = contacts.trim();
    if (!n || !c) return null;
    return { name: n, contacts: c };
  }, [name, contacts]);

  const mutation = useMutation({
    mutationFn: createRussiaCarrier,
    onSuccess: () => {
      setName('');
      setContacts('');
      invalidateRussiaCarrierCaches(qc);
      showToast({ tone: 'success', text: t('russiaCarriers:toasts.created') });
      onClose();
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const dirty = name.length > 0 || contacts.length > 0;

  return (
    <EditorModal
      open
      title={t('russiaCarriers:modals.createTitle')}
      width={520}
      dirty={dirty}
      busy={mutation.isPending}
      onConfirmClose={onClose}
    >
      <RussiaCarrierForm
        name={name}
        contacts={contacts}
        active
        onChangeName={setName}
        onChangeContacts={setContacts}
      />
      <EditorFooter>
        <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
        <Button
          kind="primary"
          disabled={payload === null || mutation.isPending}
          onClick={() => payload && mutation.mutate(payload)}
        >
          {t('common:actions.create')}
        </Button>
      </EditorFooter>
    </EditorModal>
  );
}

function RussiaCarrierEditorModal({
  row,
  canManage,
  onClose,
  onUpdated,
}: {
  row: RussiaCarrier;
  canManage: boolean;
  onClose: () => void;
  onUpdated: (row: RussiaCarrier) => void;
}) {
  const { t } = useTranslation(['russiaCarriers', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [name, setName] = useState(row.name);
  const [contacts, setContacts] = useState(row.contacts);

  const patch: UpdateRussiaCarrierBody | null = useMemo(() => {
    const trimmedName = name.trim();
    const trimmedContacts = contacts.trim();
    if (!trimmedName || !trimmedContacts) return null;
    const diff: UpdateRussiaCarrierBody = {};
    if (trimmedName !== row.name) diff.name = trimmedName;
    if (trimmedContacts !== row.contacts) diff.contacts = trimmedContacts;
    return Object.keys(diff).length === 0 ? null : diff;
  }, [name, contacts, row.name, row.contacts]);

  const patchMutation = useMutation({
    mutationFn: (body: UpdateRussiaCarrierBody) => patchRussiaCarrier(row.id, body),
    onSuccess: (next) => {
      setName(next.name);
      setContacts(next.contacts);
      invalidateRussiaCarrierCaches(qc);
      showToast({ tone: 'success', text: t('russiaCarriers:toasts.updated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveRussiaCarrier(row.id),
    onSuccess: () => {
      invalidateRussiaCarrierCaches(qc);
      showToast({ tone: 'success', text: t('russiaCarriers:toasts.archived') });
      onUpdated({ ...row, active: false });
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const activateMutation = useMutation({
    // WHY (#1166): there is no `/:id/activate` endpoint — restore is a
    //   PATCH with `active: true`, gated by the same `manage` grant.
    mutationFn: () => patchRussiaCarrier(row.id, { active: true }),
    onSuccess: (next) => {
      invalidateRussiaCarrierCaches(qc);
      showToast({ tone: 'success', text: t('russiaCarriers:toasts.activated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const busy = patchMutation.isPending || archiveMutation.isPending || activateMutation.isPending;
  const dirty = patch !== null;

  return (
    <EditorModal
      open
      title={t('russiaCarriers:modals.editTitle', { name: row.name })}
      width={520}
      dirty={dirty}
      busy={busy}
      onConfirmClose={onClose}
    >
      <RussiaCarrierForm
        name={name}
        contacts={contacts}
        active={row.active}
        readOnly={!canManage}
        onChangeName={setName}
        onChangeContacts={setContacts}
      />
      <EditorFooter>
        {canManage &&
          (row.active ? (
            <Button kind="ghost" disabled={busy} onClick={() => archiveMutation.mutate()}>
              {t('common:actions.archive')}
            </Button>
          ) : (
            <Button kind="ghost" disabled={busy} onClick={() => activateMutation.mutate()}>
              {t('common:actions.restore')}
            </Button>
          ))}
        <CancelButton onClose={onClose} label={t('common:actions.close')} />
        {canManage && (
          <Button
            kind="primary"
            disabled={patch === null || busy}
            onClick={() => patch && patchMutation.mutate(patch)}
          >
            {t('common:actions.save')}
          </Button>
        )}
      </EditorFooter>
    </EditorModal>
  );
}
